import { Editor } from "@vericus/cadmus-editor-prosemirror";

import { render } from "dom-serializer";
import { DomHandler, Element } from "domhandler";
import { find, findOne, getAttributeValue, isTag, textContent } from "domutils";
import { Parser } from "htmlparser2";
import { v4 as uuid } from "uuid";

import { PromptImage } from "@/features/multi-format/question-form/state";
import {
  BaseType,
  QuestionFieldInput,
  QuestionType,
  Shape,
  SimpleChoice,
} from "@/generated/graphql";

import { ParsedQuestion } from "../../data";
import { QuestionBankParser } from "../types";
import {
  Base64File,
  patchHTML as basePatchHTML,
  createQuestionPreviewEditor,
  getBase64Mime,
  getElementByPath,
  getParsedXML,
  getXMLFileText,
  patchMath,
} from "../utils";

const SUPPORTED_QUESTION_TYPES = [
  "multichoice",
  "truefalse",
  "shortanswer",
  "essay",
  "matching",
  "numerical",
  // FITB
  "gapselect",
  "gapfill",
  "ddwtos",
  // Hotspot
  "ddmarker",
  "ddimageortext",
] as const;
type SUPPORTED_QUESTION_TYPE = (typeof SUPPORTED_QUESTION_TYPES)[number];
const _UNSUPPORTED_QUESTION_TYPES = ["cloze", "description"] as const;
type UNSUPPORTED_QUESTION_TYPE =
  | (typeof _UNSUPPORTED_QUESTION_TYPES)[number]
  | string;

/**
 * Check if question types from moodle XML is question type that are currently supported in the system
 **/
function isSupportedQuestionType(
  questionType: string
): questionType is SUPPORTED_QUESTION_TYPE {
  return SUPPORTED_QUESTION_TYPES.includes(
    questionType as SUPPORTED_QUESTION_TYPE
  );
}

export interface MoodleQuestion {
  type: SUPPORTED_QUESTION_TYPE | UNSUPPORTED_QUESTION_TYPE;
}

export interface MoodleEmbeddedFile {
  name?: string;
  path?: string;
  data?: string;
  encoding?: string;
  parentTag?: string;
}

export interface FITBResponse {
  id: string;
  label: number;
  answer?: string;
}

export interface HotspotDropArea {
  shape: Shape;
  coords: number[];
  no: string;
  choice: string;
  text: string;
}

export interface HotspotOptionsGroup {
  no: string;
  text: string;
  identifier: string;
  group?: string;
}

// Check if xml file is a moodle XML,
// return moodle if it is otherwise return null
async function fileVersion(file: string | File) {
  const fileText = await getXMLFileText(file);
  if (!fileText) return null;
  const parsed = getParsedXML(fileText);
  if (findOne((e) => e.tagName === "quiz", parsed.childNodes)) return "moodle";
  return null;
}

// Parse moodle xml's questionText element when it's of type html
function parseRawContent(rawQuestion: Element) {
  const questionText = findOne(
    (e) => e.tagName === "questiontext",
    rawQuestion.childNodes
  );
  if (!questionText) return;
  const questionFormat = getAttributeValue(questionText, "format") ?? "html";
  // moodle have a few format for text. html, moodle_auto_format, plain_text & markdown
  // here we ignore markdown as we don't currently have markdown parser
  // TODO: parse markdown content
  if (["html", "moodle_auto_format", "plain_text"].includes(questionFormat)) {
    return parseRawHTMLContent(questionText);
  }
  return;
}

// Parse Question HTML content from moodle XML
function parseRawHTMLContent(root: Element) {
  const htmlText = textContent(
    getElementByPath(root.childNodes, ["text"]) ?? []
  );
  const handler = new DomHandler(undefined, { xmlMode: false });
  const parser = new Parser(handler, { xmlMode: false });
  parser.end(htmlText);
  const contents = patchMath([handler.root]);
  return render(contents);
}

// Parse file reference from moodle xml for embedded images, videos, etc.
function parseFiles(rawQuestion: Element): MoodleEmbeddedFile[] {
  return find(
    (e) => isTag(e) && e.tagName === "file",
    rawQuestion.childNodes,
    true,
    Number.MAX_SAFE_INTEGER
  ).flatMap((fileElement) =>
    isTag(fileElement)
      ? {
          name: getAttributeValue(fileElement, "name"),
          path: getAttributeValue(fileElement, "path"),
          encoding: getAttributeValue(fileElement, "encoding"),
          data: textContent(fileElement),
          parentTag:
            fileElement.parent && isTag(fileElement.parent)
              ? fileElement.parent.tagName
              : undefined,
        }
      : []
  );
}

// Parse tags of a question from moodle XML
function parseTags(rawQuestion: Element) {
  return find(
    (e) => isTag(e) && e.tagName === "tag",
    rawQuestion.childNodes,
    true,
    Number.MAX_SAFE_INTEGER
  ).flatMap((e) =>
    isTag(e)
      ? [textContent(getElementByPath(e.childNodes, ["text"]) ?? [])]
      : []
  );
}

// Parse question level feedbacks
function parseFeedbacks(rawQuestion: Element) {
  return {
    partial: textContent(
      getElementByPath(rawQuestion.childNodes, [
        "partiallycorrectfeedback",
        "text",
      ]) ?? []
    ),
    correct: textContent(
      getElementByPath(rawQuestion.childNodes, ["correctfeedback", "text"]) ??
        []
    ),
    incorrect: textContent(
      getElementByPath(rawQuestion.childNodes, ["incorrectfeedback", "text"]) ??
        []
    ),
    general: textContent(
      getElementByPath(rawQuestion.childNodes, ["generalfeedback", "text"]) ??
        []
    ),
  };
}

// Parse moodle XML to appropriate question type if it is supported
async function parseQuestion(
  rawQuestion: Element,
  type: MoodleQuestion["type"],
  fileOriginId: string,
  category: string
): Promise<ParsedQuestion> {
  const name = textContent(
    getElementByPath(rawQuestion.childNodes, ["name", "text"]) ?? []
  );
  const content = parseRawContent(rawQuestion);
  const files = parseFiles(rawQuestion);
  const tags = parseTags(rawQuestion);
  const shuffle = /true/gi.test(
    textContent(
      getElementByPath(rawQuestion.childNodes, ["shuffleanswers"]) ?? []
    )
  );
  const feedbacks = parseFeedbacks(rawQuestion);
  const points = Math.ceil(
    parseFloat(
      textContent(
        findOne(
          (e) => e.tagName === "defaultgrade",
          rawQuestion.childNodes,
          false
        ) ?? []
      )
    )
  );

  const promptEditor = await getPromptEditor(content ?? "", files);
  const question: ParsedQuestion = {
    fileOriginId,
    questionId: uuid(),
    questionType: null,
    shortPrompt: name ?? promptEditor?.getText()?.slice?.(0, 50) ?? "",
    prompt: JSON.stringify(promptEditor?.getJSON() ?? ""),
    points,
    category,
    feedback: feedbacks.general,
    tags,
    shuffle,
    fields: [],
  };
  if (!isSupportedQuestionType(type)) {
    return question;
  }

  switch (type) {
    case "multichoice":
      return await parseMCQ(rawQuestion, question, files, points);
    case "truefalse":
      return await parseTF(rawQuestion, question);
    case "shortanswer":
      return await parseShortAnswer(rawQuestion, question);
    case "essay":
      return await parseEssay(rawQuestion, question);
    case "numerical":
      return await parseNumerical(rawQuestion, question);
    case "matching":
      return await parseMatching(rawQuestion, question, files, points);
    case "gapselect":
      return await parseGapSelect(
        rawQuestion,
        question,
        files,
        points,
        promptEditor
      );
    case "gapfill":
      return await parseGapFill(
        rawQuestion,
        question,
        files,
        points,
        promptEditor
      );
    case "ddwtos":
      return await parseGapSelect(
        rawQuestion,
        question,
        files,
        points,
        promptEditor
      );
    case "ddimageortext":
      return await parseImageOrTextHotspot(
        rawQuestion,
        question,
        files,
        points,
        promptEditor
      );
    case "ddmarker":
      return await parseMarkerHotspot(
        rawQuestion,
        question,
        files,
        points,
        promptEditor
      );
  }
}

// Get all answer elements
function getAnswers(rawQuestion: Element) {
  return find(
    (e) => isTag(e) && e.tagName === "answer",
    rawQuestion.childNodes,
    true,
    Number.MAX_SAFE_INTEGER
  );
}

// Parse moodle xml for matching question
async function parseMatching(
  rawQuestion: Element,
  question: ParsedQuestion,
  files: MoodleEmbeddedFile[],
  // TODO: use points when partial scoring is more flexible with
  // each option can be of a different weighting
  _points: number
): Promise<ParsedQuestion> {
  const targetMap = new Map<string, string>();
  const sourceMap = new Map<string, string>();
  const correctValues: string[] = [];

  const subQuestions = find(
    (e) => isTag(e) && e.tagName === "subquestion",
    rawQuestion.childNodes,
    true,
    Number.MAX_SAFE_INTEGER
  ) as Element[];

  await Promise.all(
    subQuestions.map(async (e) => {
      const targetIdentifier = uuid();

      const promptContent = parseRawHTMLContent(e) ?? "";
      const promptEditor = await getPromptEditor(promptContent, files);

      const prompt = promptEditor.getText();

      // if subq has content, then it is a target
      if (prompt) {
        targetMap.set(targetIdentifier, prompt);
      }

      const answer = findOne(
        (e) => isTag(e) && e.tagName === "answer",
        e.childNodes
      );

      const sourceIdentifier = uuid();

      // put the answer in the source list
      const answerContent = answer ? (parseRawHTMLContent(answer) ?? "") : "";
      const answerEditor = await getPromptEditor(answerContent, files);
      const answerText = answerEditor.getText();

      if (answerText) {
        sourceMap.set(sourceIdentifier, answerText);
      }

      if (prompt && answerText) {
        correctValues.push(`${targetIdentifier} ${sourceIdentifier}`);
      }
    })
  );
  const field: QuestionFieldInput = {
    identifier: "RESPONSE",
    matchInteraction: {
      sourceSet: Array.from(sourceMap.entries()).map(
        ([sourceId, sourceContent]) => ({
          identifier: sourceId,
          content: sourceContent,
        })
      ),
      targetSet: Array.from(targetMap.entries()).map(
        ([targetId, targetContent]) => ({
          identifier: targetId,
          content: targetContent,
        })
      ),
    },
    response: {
      correctValues,
    },
  };

  return {
    ...question,
    questionType: QuestionType.Matching,
    fields: [field],
  };
}

async function getPromptImage(
  files: MoodleEmbeddedFile[]
): Promise<PromptImage | null> {
  // only support base64 image for now
  const maybeHotspotImages = files.filter(
    (f) => f.parentTag === "question" && f.encoding === "base64"
  );
  if (maybeHotspotImages.length !== 1 || !maybeHotspotImages[0]?.data)
    return null;

  const mime = getBase64Mime(maybeHotspotImages[0]!.data!);
  const imageURL = `data:${mime};base64,${maybeHotspotImages[0]!.data!}`;
  const { width, height } = await new Promise<{
    width: number;
    height: number;
  }>((resolve) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.width, height: img.height });
    img.src = imageURL;
  });

  return {
    url: imageURL,
    height,
    width,
    layers: [],
  };
}

function getHotspotOptionsGroup(rawQuestion: Element): HotspotOptionsGroup[] {
  const options = find(
    (e) => isTag(e) && e.tagName === "drag",
    rawQuestion.childNodes,
    true,
    Number.MAX_SAFE_INTEGER
  ) as Element[];
  return options
    .flatMap((option) => {
      const groupKeyElement = getElementByPath(option.childNodes, [
        "draggroup",
      ]);
      const groupChoiceElement = getElementByPath(option.childNodes, ["text"]);
      const groupOrderElement = getElementByPath(option.childNodes, ["no"]);

      if (groupChoiceElement && groupOrderElement) {
        const text = textContent(groupChoiceElement);
        const no = textContent(groupOrderElement);
        if (groupKeyElement) {
          const group = textContent(groupKeyElement);
          return [{ group, text, no, identifier: uuid() }];
        } else {
          return [{ text, no, identifier: uuid() }];
        }
      }
      return [];
    })
    .filter(({ text }) => text && text !== "");
}

function getHotspotDropAreas(rawQuestion: Element) {
  const drops = find(
    (e) => isTag(e) && e.tagName === "drop",
    rawQuestion.childNodes,
    true,
    Number.MAX_SAFE_INTEGER
  ) as Element[];
  return Object.entries(
    drops
      .flatMap((drop) => {
        const shapeElement = getElementByPath(drop.childNodes, ["shape"]);
        const shape = shapeElement ? textContent(shapeElement) : "point";
        const no = textContent(getElementByPath(drop.childNodes, ["no"]) ?? []);
        const choice = textContent(
          getElementByPath(drop.childNodes, ["choice"]) ?? []
        );
        const text = textContent(
          getElementByPath(drop.childNodes, ["text"]) ?? []
        );
        let coords: number[] = [];
        if (shape === "point") {
          const xLeft = textContent(
            getElementByPath(drop.childNodes, ["xleft"]) ?? []
          );
          const yTop = textContent(
            getElementByPath(drop.childNodes, ["ytop"]) ?? []
          );
          if (xLeft !== "" && yTop !== "") {
            coords = [xLeft, yTop].map(Number);
          }
        } else {
          coords = textContent(
            getElementByPath(drop.childNodes, ["coords"]) ?? []
          )
            .split(";")
            .flatMap((c) => {
              const splitted = c.split(",");
              return c !== "" && splitted.length ? splitted.map(Number) : [];
            });
        }
        if (!coords.length) return [];
        return [
          {
            shape,
            coords,
            no,
            choice,
            text,
          } as HotspotDropArea,
        ];
      })
      .reduce(
        (acc, drop) => {
          if (acc[drop.choice]) {
            acc[drop.choice]!.push(drop);
          } else {
            acc[drop.choice] = [drop];
          }
          return acc;
        },
        {} as Record<string, HotspotDropArea[]>
      )
  );
}

// Parse moodle xml for parsing hotspot
async function parseImageOrTextHotspot(
  rawQuestion: Element,
  question: ParsedQuestion,
  files: MoodleEmbeddedFile[],
  _points: number,
  editor: Editor
): Promise<ParsedQuestion> {
  const fields: QuestionFieldInput[] = [];

  const promptImage = await getPromptImage(files);
  if (!promptImage) return { ...question, questionType: null };

  const optionGroups = getHotspotOptionsGroup(rawQuestion);

  getHotspotDropAreas(rawQuestion).forEach(([choice, areas]) => {
    const responseId = uuid();
    const correctChoices = optionGroups.filter(({ no }) => no === choice);
    areas.forEach((area) => {
      promptImage.layers.push({
        identifier: responseId,
        coords: area.coords,
        shape: area.shape,
      });
    });

    const field: QuestionFieldInput = {
      identifier: responseId,
      response: {
        correctValues: correctChoices.map(({ identifier }) => identifier),
      },
    };
    const maybeChoiceGroups = new Set(
      correctChoices.flatMap(({ group }) => group)
    );
    if (maybeChoiceGroups.size <= 1) {
      const group = maybeChoiceGroups.values().next().value;
      const choices = optionGroups.filter(({ group: optionGroup }) =>
        group ? group === optionGroup : true
      );
      if (choices) {
        field.choiceInteraction = {
          choices: choices.map((choice) => ({
            content: choice.text,
            identifier: choice.identifier,
          })),
          maxChoices: 1,
        };
      }
      fields.push(field);
    }
  });
  return {
    ...question,
    prompt: JSON.stringify(editor?.getJSON() ?? ""),
    promptImage,
    questionType: fields.some(
      ({ response: { correctValues } }) => correctValues.length < 1
    )
      ? null
      : QuestionType.Hotspot,
    fields,
  };
}

async function parseMarkerHotspot(
  rawQuestion: Element,
  question: ParsedQuestion,
  files: MoodleEmbeddedFile[],
  _points: number,
  editor: Editor
): Promise<ParsedQuestion> {
  const fields: QuestionFieldInput[] = [];

  const promptImage = await getPromptImage(files);
  if (!promptImage) return { ...question, questionType: null };

  const optionGroups = getHotspotOptionsGroup(rawQuestion);

  getHotspotDropAreas(rawQuestion).forEach(([choice, areas]) => {
    const responseId = uuid();
    const correctChoices = optionGroups.filter(({ no }) => no === choice);
    const correctAreas = areas.map((area) => ({
      identifier: responseId,
      coords: area.coords,
      shape: area.shape,
    }));

    const field: QuestionFieldInput = {
      identifier: responseId,
      response: {
        correctValues: [],
        correctAreas,
      },
    };
    if (correctChoices[0]?.text) {
      field.positionObjectInteraction = {
        label: correctChoices[0].text,
        maxChoices: 1,
      };
      fields.push(field);
    }
  });
  return {
    ...question,
    prompt: JSON.stringify(editor?.getJSON() ?? ""),
    promptImage,
    questionType: fields.some(
      ({ response: { correctAreas }, positionObjectInteraction }) =>
        (correctAreas?.length ?? 0) < 1 && positionObjectInteraction?.label
    )
      ? null
      : QuestionType.Hotspot,
    fields,
  };
}

// Parse moodle xml for parsing gap select
async function parseGapSelect(
  rawQuestion: Element,
  question: ParsedQuestion,
  _files: MoodleEmbeddedFile[],
  _points: number,
  editor: Editor
): Promise<ParsedQuestion> {
  const options = find(
    (e) => isTag(e) && ["selectoption", "dragbox"].includes(e.tagName),
    rawQuestion.childNodes,
    true,
    Number.MAX_SAFE_INTEGER
  ) as Element[];
  let hasBlank = false;
  const blankRegex = /\[\[(\d+)\]\]/g;
  const fitbReponses: FITBResponse[] = [];
  while ((editor.getText().match(blankRegex)?.length ?? 0) > 0) {
    hasBlank = true;
    let replaced = false;
    editor.state.doc.nodesBetween(
      0,
      editor.state.doc.content.size,
      (node, pos) => {
        if (node.isText && !replaced && node.text) {
          const match = blankRegex.exec(node.text);
          if (match?.length && match[1]) {
            const id = uuid();
            fitbReponses.push({ id, label: parseInt(match[1], 10) });
            editor.commands.insertContentAt(
              {
                from: pos + match.index,
                to: pos + match.index + match[0].length,
              },
              `<span data-cadmus-blank="0" id="${id}">_____</span>`
            );
            replaced = true;
          }
          return false;
        }
        if (replaced) return false;
        return true;
      }
    );
  }

  if (!hasBlank) return question;

  const optionGroups = options.flatMap((option) => {
    const groupKeyElement = getElementByPath(option.childNodes, ["group"]);
    const groupChoiceElement = getElementByPath(option.childNodes, ["text"]);
    if (groupKeyElement && groupChoiceElement) {
      const group = textContent(groupKeyElement);
      const text = textContent(groupChoiceElement);
      return [{ group, text }];
    }
    return [];
  });

  const optionGroupsWithCorrectValue = [...Array(fitbReponses.length)].flatMap(
    (_, index) => {
      const correctOptionGroup = optionGroups[index];
      if (!correctOptionGroup) return [];
      const choices = optionGroups
        .filter(({ group }) => group === correctOptionGroup.group)
        .map(({ text }) => text);
      return [{ correctValue: correctOptionGroup.text.trim(), choices }];
    }
  );
  const fields: QuestionFieldInput[] = [];
  fitbReponses.forEach(({ label, id }) => {
    const optionIndex = label - 1;
    if (!optionGroupsWithCorrectValue[optionIndex]) return;
    const { correctValue, choices } =
      optionGroupsWithCorrectValue[optionIndex]!;

    const textBlank = choices?.length === 1;

    fields.push({
      identifier: id,
      ...(textBlank && { textEntryInteraction: {} }),
      ...(!textBlank && {
        choiceInteraction: {
          choices: choices.map((phrase: string) => ({
            identifier: phrase.trim(),
            content: phrase.trim(),
          })),
          maxChoices: 1,
        },
      }),
      response: {
        // moodle only supports one correct
        // answer per gap select
        correctValues: [correctValue],
        matchSimilarity: 1,
        caseSensitive: false,
      },
    } as QuestionFieldInput);
  });

  return {
    ...question,
    prompt: JSON.stringify(editor?.getJSON() ?? ""),
    questionType: QuestionType.Blanks,
    fields,
  };
}

// Parse moodle xml for parsing gap select
async function parseGapFill(
  _rawQuestion: Element,
  question: ParsedQuestion,
  _files: MoodleEmbeddedFile[],
  _points: number,
  editor: Editor
): Promise<ParsedQuestion> {
  let hasBlank = false;
  const blankRegex = /\[(.*)\]/g;
  const fitbReponses: FITBResponse[] = [];
  while ((editor.getText().match(blankRegex)?.length ?? 0) > 0) {
    hasBlank = true;
    let replaced = false;
    let blankIndex = 1;
    editor.state.doc.nodesBetween(
      0,
      editor.state.doc.content.size,
      (node, pos) => {
        if (node.isText && !replaced && node.text) {
          const match = blankRegex.exec(node.text);
          if (match?.length && match[1]) {
            const id = uuid();
            fitbReponses.push({ id, label: blankIndex, answer: match[1] });
            editor.commands.insertContentAt(
              {
                from: pos + match.index,
                to: pos + match.index + match[0].length,
              },
              `<span data-cadmus-blank="0" id="${id}">_____</span>`
            );
            replaced = true;
          }
          blankIndex++;
          return false;
        }
        if (replaced) return false;
        return true;
      }
    );
  }

  if (!hasBlank) return question;

  const fields: QuestionFieldInput[] = [];
  fitbReponses.forEach(({ answer, id }) => {
    fields.push({
      identifier: id,
      textEntryInteraction: {},
      response: {
        // moodle only supports one correct
        // answer per gap select
        correctValues: [answer],
        matchSimilarity: 1,
        caseSensitive: false,
      },
    } as QuestionFieldInput);
  });

  return {
    ...question,
    prompt: JSON.stringify(editor?.getJSON() ?? ""),
    questionType: QuestionType.Blanks,
    fields,
  };
}

// Parse moodle xml MCQ question
async function parseMCQ(
  rawQuestion: Element,
  question: ParsedQuestion,
  files: MoodleEmbeddedFile[],
  // TODO: use points when partial scoring is more flexible with
  // each option can be of a different weighting
  _points: number
): Promise<ParsedQuestion> {
  const answers = getAnswers(rawQuestion);

  const answerIds = new Set<string>();
  function* idGenerator(): Generator<string> {
    while (true) {
      let id = uuid();
      while (answerIds.has(id)) {
        id = uuid();
      }
      answerIds.add(id);
      yield id;
    }
  }
  const answerIdGenerator = idGenerator();
  const getId = () => answerIdGenerator.next().value;

  const correctValues: string[] = [];
  const maxChoices = correctValues.length;

  const choices: SimpleChoice[] = await Promise.all(
    answers.map(async (e) => {
      const identifier = getId();
      const answerContent = parseRawHTMLContent(e as Element);
      const answerEditor = await getPromptEditor(answerContent ?? "", files);

      if (parseFloat(getAttributeValue(e as Element, "fraction") ?? "") > 0) {
        correctValues.push(identifier.toString());
      }
      return {
        identifier,
        content: answerEditor.getText(),
      };
    })
  );

  const field: QuestionFieldInput = {
    identifier: "RESPONSE",
    choiceInteraction: {
      choices,
      maxChoices,
    },
    response: {
      correctValues,
    },
  };

  return {
    ...question,
    questionType: QuestionType.Mcq,
    fields: [field],
  };
}

// Parse moodle xml True and False question
async function parseTF(
  rawQuestion: Element,
  question: ParsedQuestion
): Promise<ParsedQuestion> {
  const answers = getAnswers(rawQuestion);
  if (!answers?.length) return question;
  const answer = findOne(
    (e) => isTag(e) && parseFloat(getAttributeValue(e, "fraction") ?? "") > 0,
    answers
  );
  if (!answer) return question;

  const field: QuestionFieldInput = {
    identifier: "RESPONSE",
    choiceInteraction: {
      choices: [
        { identifier: "true", content: "True" },
        { identifier: "false", content: "False" },
      ],
      maxChoices: 1,
    },
    response: {
      correctValues: [
        /true/gi
          .test(
            textContent(getElementByPath(answer.childNodes, ["text"]) ?? [])
          )
          .toString(),
      ],
    },
  };

  return {
    ...question,
    questionType: QuestionType.Truefalse,
    fields: [field],
  };
}

// Parse moodle xml extended answer question
async function parseEssay(
  rawQuestion: Element,
  question: ParsedQuestion
): Promise<ParsedQuestion> {
  const wordLimit = parseInt(
    textContent(
      findOne((e) => e.tagName === "maxwordlimit", rawQuestion.childNodes) ?? []
    ),
    10
  );

  const field: QuestionFieldInput = {
    identifier: "RESPONSE",
    extendedInteraction: {
      wordLimit,
    },
    response: {
      correctValues: [],
    },
  };

  return {
    ...question,
    questionType: QuestionType.Extended,
    fields: [field],
  };
}

// Parse moodle xml short answer question
async function parseShortAnswer(
  rawQuestion: Element,
  question: ParsedQuestion
): Promise<ParsedQuestion> {
  const answers = getAnswers(rawQuestion).filter(
    (e) => parseFloat(getAttributeValue(e as Element, "fraction") ?? "") > 0
  );
  if (!answers?.length) return question;

  const field: QuestionFieldInput = {
    identifier: "RESPONSE",
    textEntryInteraction: {},
    response: {
      correctValues: answers.map((e) =>
        textContent(findOne((e) => e.tagName === "text", [e]) ?? [])
      ),
      matchSimilarity: 1,
      caseSensitive:
        textContent(
          findOne((e) => e.tagName === "usecase", rawQuestion.childNodes) ?? []
        ) === "1",
    },
  };

  return {
    ...question,
    questionType: QuestionType.Short,
    fields: [field],
  };
}

// Parse moodle xml numerical question
async function parseNumerical(
  rawQuestion: Element,
  question: ParsedQuestion
): Promise<ParsedQuestion> {
  const answers = getAnswers(rawQuestion).filter(
    (e) => parseFloat(getAttributeValue(e as Element, "fraction") ?? "") > 0
  );
  if (!answers?.length) return question;

  const field: QuestionFieldInput = {
    identifier: "RESPONSE",
    textEntryInteraction: {},
    response: {
      correctValues: answers.map((e) =>
        textContent(findOne((e) => e.tagName === "text", [e]) ?? [])
      ),
      errorMargin: 0,
      baseType: BaseType.Float,
    },
  };

  return {
    ...question,
    questionType: QuestionType.Numerical,
    fields: [field],
  };
}

// Get prompt texts
async function getPromptEditor(content: string, files: MoodleEmbeddedFile[]) {
  const promptHTML = (await patchHTML(content, files)) ?? null;
  return createQuestionPreviewEditor({
    id: "MoodleParserEditor",
    promptDoc: promptHTML,
  });
}

// filepath to base64 string conversion to convert file reference to a file
// for images, video, etc.
function getBase64File(files: MoodleEmbeddedFile[]) {
  return async (src: string): Promise<Base64File | null> => {
    if (files.length === 0) return null;
    const getUrl = window.location;
    const baseUrl =
      getUrl.origin + getUrl.pathname.split("/").slice(0, -1).join("/");
    const imgSrc = src.replace(baseUrl, "");
    const srcRegex = /(\/@@PLUGINFILE@@\/?([^"]+))|(.*)/;
    const match = srcRegex.exec(imgSrc);
    if (match?.[2]) {
      const filename = decodeURI(match[2]);
      const file = files.find(
        (f) => f.name === filename && f.encoding === "base64" && f.data
      );
      if (file?.data && file?.name) {
        return {
          base64String: file.data,
          filename: file.name,
        };
      }
    }
    return null;
  };
}

async function patchHTML(str: string, files: MoodleEmbeddedFile[]) {
  return await basePatchHTML(str, getBase64File(files));
}

export const parser: QuestionBankParser<"moodle"> = {
  parse: async (file, _onProcessingFiles, onProcessedFiles, _version) => {
    const fileOriginId = uuid();
    const fileText = await getXMLFileText(file);
    if (!fileText) return [];
    const parsed = getParsedXML(fileText);
    const quiz = findOne((e) => e.tagName === "quiz", parsed.childNodes);
    const maybeQuestions = find(
      (e) => isTag(e) && e.tagName === "question",
      quiz?.childNodes ?? [],
      false,
      Number.MAX_SAFE_INTEGER
    );
    let category = "";
    const questions: ParsedQuestion[] = [];
    for (const maybeQuestion of maybeQuestions) {
      if (!isTag(maybeQuestion)) continue;
      const type = getAttributeValue(maybeQuestion, "type");
      if (!type) continue;
      // if question type is category then set category of subsequent questions
      if (type === "category") {
        category = textContent(
          getElementByPath(maybeQuestion.childNodes, ["category", "text"]) ?? []
        );
      } else {
        questions.push(
          await parseQuestion(maybeQuestion, type, fileOriginId, category)
        );
      }
    }
    onProcessedFiles(1);
    return [
      {
        fileOrigin: {
          fileOriginId,
          filepath: file instanceof File ? (file.name ?? "") : "",
          filename: file instanceof File ? (file?.name ?? "") : "",
        },
        questions,
      },
    ];
  },
  fileVersion,
  maybeParsable: async (file: string | File) =>
    (await fileVersion(file)) !== null,
};
